<script setup>
import { createDirectory, createDirectoryExternial, getCount } from '@/api/generalApi';
import SocialMediaLinks from '@/components/External/SocialMediaLinks.vue';
import MakeRequestModal from '@/components/External/MakeRequestModal.vue';
import { showNotification } from '@/helper/helper';
import { ref, onMounted } from "vue";
import { useDisplay } from 'vuetify';

const { smAndDown, lgAndDown } = useDisplay();
const makeRequest = ref(false);
const vacanciesCount = ref(null);

const sendForm = async (userData, selectedVacancy, isAuth, done) => {
    const arrayData = Object.values(userData);
    const dataObject = {
        journal: {
            abc_code: 5501
        },
        indicator: arrayData
    };

    if (isAuth) {
        if (selectedVacancy) {
            dataObject.journal.parent_id = selectedVacancy;
        }
        await createDirectory('feedback', dataObject)
            .then(res => {
                if (res) {
                    done(true);
                    makeRequest.value = false;
                    showNotification('success', 'Информация отправлена успешно');
                }
            });
    }
    else {
        await createDirectoryExternial('feedback', dataObject)
            .then(res => {
                if (res) {
                    done(true);
                    makeRequest.value = false;
                    showNotification('success', 'Информация отправлена успешно');
                }
            });
    }
};

onMounted(async () => {
    await getCount('?vacancy_status=open')
        .then(res => {
            if (res && res.data) vacanciesCount.value = res.data.vacancies_count;
        });
});
</script>

<template>
    <div class="w-full relative" :class="smAndDown ? 'mt-10' : 'mt-3'">
        <img 
            v-if="!smAndDown"
            src="/images/dots.svg" 
            class="absolute bottom-0 left-6 h-full opacity-[42%] py-8"
        />
        
        <div 
            v-if="smAndDown"
            class="flex flex-col items-center px-3"
        >
            <div class="flex items-center opacity-[54%]">
                <span>
                    {{ $t('main.now') }} {{ $t('main.on_site') }} {{ $t('main.vacancies', 2) }}:
                </span>
                <span class="text-[30px] ml-3">{{ vacanciesCount }}</span>
            </div>
            <router-link 
                to="/vacancies"
                class="text-[#418AD1] underline mt-3 mb-5"
            >
                {{ $t('main.view_all_vacancies') }}
            </router-link>
            <v-btn 
                :text="$t('main.leave_request')"
                prepend-icon="mdi-email-outline"
                color="#127CC1"
                rounded="lg"
                height="44"
                width="280"
                class="mb-5"
                @click="makeRequest = true"
            />
            <SocialMediaLinks />
            <span class="opacity-[54%] my-5">
                &copy; work.kmg.kz 2023
            </span>
        </div>

        <div 
            v-else
            class="w-full pt-6 pb-6 flex items-end"
            :class="lgAndDown ? 'pl-36 pr-8' : 'px-36'"
        >
            <div 
                class="w-full grid grid-cols-4 grid-rows-[60px 200px] gap-y-4"
                :class="lgAndDown ? 'gap-x-6' : 'gap-x-10'"
            >
                <div class="flex items-center">
                    <img 
                        src="/images/kmg_logo_bw.svg" 
                        class="opacity-70" 
                        :class="lgAndDown ? 'w-[160px]' : 'w-[280px]'"
                    />
                </div>
                <div class="flex items-center">
                    <span class="uppercase text-xl font-semibold">
                        {{ $t('main.info') }}
                    </span>
                </div>
                <div class="flex items-center">
                    <span class="uppercase text-xl font-semibold">
                        {{ $t('main.contacts') }}
                    </span>
                </div>
                <div class="flex items-center">
                    <span class="uppercase text-xl font-semibold">
                        {{ $t('main.requests') }}
                    </span>
                </div>

                <div class="flex flex-col justify-between leading-5">
                    <div>
                        <div class="flex items-center opacity-[54%]" :class="{ 'text-[14px]': lgAndDown }">
                            <span class="w-3/5">
                                {{ $t('main.now') }} {{ $t('main.on_site') }} {{ $t('main.vacancies', 2) }}:
                            </span>
                            <span 
                                v-if="vacanciesCount"
                                class="ml-8"
                                :class="lgAndDown ? 'text-2xl' : 'text-[40px]'"
                            >
                                {{ vacanciesCount }}
                            </span>
                        </div>
                        <router-link 
                            to="/vacancies"
                            class="text-[#418AD1] underline inline-block mt-4"
                        >
                            {{ $t('main.view_all_vacancies') }}
                        </router-link>
                    </div>
                    <span class="opacity-[54%] mt-16 leading-3">
                        &copy; work.kmg.kz 2023
                    </span>
                </div>
                <div class="flex flex-col opacity-[54%] leading-5" :class="{ 'text-[14px]': lgAndDown }">
                    <router-link to="/about" class="hover:underline">
                        {{ $t('main.about_company') }}
                    </router-link>
                    <router-link to="/steps" class="hover:underline first-letter:uppercase">
                        {{ $t('main.selection_stages') }}
                    </router-link>
                    <router-link to="/for_students" class="hover:underline">
                        {{ $t('general.for_students') }}
                    </router-link>
                    <router-link to="/faq" class="hover:underline">
                        {{ $t('main.faq') }}
                    </router-link>
                </div>
                <div class="flex flex-col leading-5" :class="{ 'text-[14px]': lgAndDown }">
                    <span>{{ $t('main.recruitment_questions') }}</span>
                    <!-- <span class="opacity-[54%]">
                        {{ $t('fields.email') }}:
                        <a href="mailto:a.kulmagambetova@kmg.kz" target="__blank" class="text-[#418AD1]">
                            a.kulmagambetova@kmg.kz
                        </a>
                    </span>
                    <span class="opacity-[54%]">
                        {{ $t('fields.phone') }}: 8 7172 78 90 54
                    </span> -->
                    <span class="opacity-[54%] block mt-1">
                        {{ $t('fields.email') }}:
                        <a href="mailto:k.zhurtekina@kmg.kz" target="__blank" class="text-[#418AD1]">
                            k.zhurtekina@kmg.kz
                        </a>
                    </span>
                    <span class=" opacity-[54%]">
                        {{ $t('fields.phone') }}: 8 7172 78 92 99
                    </span>
                    <span class="mt-3">{{ $t('main.support_contacts') }}</span>
                    <span class="opacity-[54%]">
                        {{ $t('fields.email') }}:
                        <a href="mailto:sms@kmg.kz" target="__blank" class="text-[#418AD1]">
                            sms@kmg.kz
                        </a>
                    </span>
                    <span class=" opacity-[54%]">
                        {{ $t('fields.phone') }}: 8 800 080 1444
                    </span>
                  <span class=" opacity-[54%]">
                        {{ $t('fields.phone_city') }}: 1444
                    </span>
                    <span class="mt-3">{{ $t('main.students_contacts') }}:</span>
                    <span class="opacity-[54%]">
                        {{ $t('fields.email') }}:
                        <a href="mailto:n.gallam@kmg.kz" target="__blank" class="text-[#418AD1]">
                            n.gallam@kmg.kz
                        </a>
                    </span>
                    <span class=" opacity-[54%]">
                        {{ $t('fields.phone') }}: 8 7172 78 63 88
                    </span>
                </div>
                <div class="flex flex-col justify-between">
                    <v-btn 
                        :text="$t('main.leave_request')"
                        rounded="lg"
                        height="44"
                        :prepend-icon="lgAndDown ? '' : 'mdi-email-outline'"
                        color="#127CC1"
                        :size="lgAndDown ? 'x-small' : 'large'"
                        :width="lgAndDown ? '200' : '280'"
                        :class="{ 'text-none text-sm': lgAndDown }"
                        @click="makeRequest = true"
                    />
                    <div v-if="lgAndDown" class="flex justify-end">
                        <SocialMediaLinks />
                    </div>
                </div>
            </div>
            <SocialMediaLinks v-if="!lgAndDown" />
        </div>
       
        <MakeRequestModal 
            v-if="makeRequest"
            v-model="makeRequest"
            @close="makeRequest = false"
            @sendForm="sendForm"
        />
    </div>
</template>